<template>
  <div class="job-architecture-page">
    <div class="job-architecture-breadcrumb-row">
      <router-link
        :to="{ name: 'job-architecture', query: { view: 'list' } }"
        class="breadcrumb-back-link text-body-2"
      >
        Job Architecture
      </router-link>
      <span class="breadcrumb-separator text-body-2">/</span>
      <span class="text-body-2 text-medium-emphasis">Compare profiles</span>
    </div>

    <div class="job-architecture-header">
      <h2 class="text-h1">
        Compare Job Profiles
      </h2>
      <p class="text-body-2 text-medium-emphasis">
        Compare selected job profiles side by side.
      </p>
    </div>

    <div class="job-architecture-controls-row">
      <div class="job-architecture-filters-left">
        <ColumnsFilterDropdown
          v-if="tableControls"
          :columns="tableControls.profileColumns"
          :visibility="tableControls.columnVisibilityState"
          @toggle-column="tableControls.toggleColumnVisibility"
          @select-all="tableControls.setAllColumnsVisible"
        />
        <RowsFilterDropdown
          v-if="tableControls"
          :fields="tableControls.orderedFieldsForRows || tableControls.allFields"
          :visible-ids="tableControls.visibleFieldIdsArray"
          @toggle-field="tableControls.toggleFieldVisibility"
          @select-all="tableControls.setAllRowsVisible"
        />
      </div>
      <div class="job-architecture-filters-right">
        <v-btn
          variant="outlined"
          rounded="lg"
          class="filter-style-btn"
          :disabled="!tableControls"
          @click="tableControls?.downloadCsv?.({ fileName: csvDownloadFileName })"
        >
          <v-icon icon="mdi-download" start />
          Download CSV
        </v-btn>
      </div>
    </div>

    <template v-if="noSelection">
      <p class="text-medium-emphasis pa-4">
        No job profiles selected. Select job profiles in the
        <router-link :to="{ name: 'job-architecture', query: { view: 'list' } }" class="breadcrumb-back-link">
          list view
        </router-link>
        and click “Compare selected”.
      </p>
    </template>

    <template v-else>
      <JobProfileComparisonTable
        v-if="profiles.length > 0"
        :profiles="profiles"
        @update:controls="tableControls = $event"
      />
      <p
        v-else-if="profilesLoading"
        class="text-medium-emphasis pa-4"
      >
        Loading profiles…
      </p>
      <p
        v-else-if="profilesError"
        class="text-medium-emphasis pa-4"
      >
        Failed to load profiles. Please try again.
      </p>
      <p
        v-else
        class="text-medium-emphasis pa-4"
      >
        No profile data could be loaded for the selected profiles.
      </p>
    </template>
  </div>
</template>

<script setup>
import { format } from 'date-fns';
import {
  ref, computed, onMounted,
} from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { getProfilesByUlids } from '@/api/architecture';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { normalizeCustomFields } from '@/stores/jobArchitecture/helpers';
import JobProfileComparisonTable from '@/components/jobArchitecture/JobProfileComparisonTable.vue';
import ColumnsFilterDropdown from '@/components/jobArchitecture/ColumnsFilterDropdown.vue';
import RowsFilterDropdown from '@/components/jobArchitecture/RowsFilterDropdown.vue';

const route = useRoute();
const { setLoading } = useAppLoading();
const { listLoading: profilesLoading } = storeToRefs(useAppLoading());

const tableControls = ref(null);
const profiles = ref([]);
const profilesError = ref(false);

const profileUlids = computed(() => {
  const raw = route.query?.profiles || '';

  return raw.split(',').map((s) => s.trim()).filter(Boolean);
});

const noSelection = computed(() => profileUlids.value.length === 0);

const csvDownloadFileName = computed(() => `job-profiles-compare-${format(new Date(), 'MM-dd-yyyy')}.csv`);

const loadProfiles = async () => {
  const ulids = profileUlids.value;

  if (ulids.length === 0) {
    profiles.value = [];
    profilesError.value = false;

    return;
  }

  setLoading({ listLoading: true });

  profilesError.value = false;

  try {
    const data = await getProfilesByUlids(ulids);

    profiles.value = (data || []).map((profile) => {
      const customFields = normalizeCustomFields(profile.custom_fields);

      return { ...profile, custom_fields: customFields };
    });
  } catch (err) {
    useAppSnackbar().addErrorSnackbar({
      text: `Job profiles load error: ${err.message || err}`,
    });

    profiles.value = [];
    profilesError.value = true;
  } finally {
    setLoading({ listLoading: false });
  }
};

onMounted(() => {
  loadProfiles();
});
</script>

<style scoped lang="less">
@import '@/less/mixins';

.job-architecture-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  padding: 24px 24px 16px;
}

.job-architecture-header {
  margin-bottom: 24px;
}

.job-architecture-controls-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.job-architecture-breadcrumb-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.breadcrumb-back-link {
  color: @text-brand;
  text-decoration: none;
}

.breadcrumb-back-link:hover {
  text-decoration: underline;
}

.breadcrumb-separator {
  color: @grey-500;
}

.job-architecture-filters-left,
.job-architecture-filters-right {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.filter-style-btn {
  height: 40px !important;
  min-height: 40px !important;
  background-color: @bg-default !important;
  border: 1px solid @stroke-default;
  color: @text-darkest;
  font-weight: 400;
  text-transform: none;
}
</style>
